// Shared shop-floor notes for a CUSTOMER ORDER, editable from any of its
// work-order rows.
//
// One note per order (CustomerOrder.factory_notes) — every WO row of the
// order shows the same text. Autosaves as the user types (800ms debounce,
// last write wins) with a live "Saving… / Saved HH:MM" indicator. Other
// terminals pick the note up on their next data refresh; on mount the
// box seeds from the row payload (customer_order_notes).
//
// PERF: compact (in-table) mode renders a read-only preview until clicked.
// MUI's multiline TextField mounts a TextareaAutosize that force-measures
// layout on mount — one per table row froze the all-operations page
// ("page isn't responding") on slower browsers. The real editor only
// mounts for the row the operator actually clicks.

import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { StickyNote2 } from '@mui/icons-material';

import { v2 } from '../../../../services/api';

const DEBOUNCE_MS = 800;

// Broadcast channel keeping every visible copy of an order's note in sync
// (the collapsed-row badge + any other open editor) as the user types —
// same-page only; other terminals sync on their next data refresh.
export const NOTES_EVENT = 'gopak:order-notes';

export const broadcastOrderNotes = (orderId, notes) => {
  window.dispatchEvent(new CustomEvent(NOTES_EVENT, { detail: { orderId, notes } }));
};

export default function OrderFactoryNotesV2({
  orderId, initialNotes = '', compact = false,
}) {
  const [text, setText] = useState(initialNotes || '');
  // idle | dirty | saving | saved | error
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);
  // Compact mode: false until the operator clicks the preview — the heavy
  // autosizing textarea only exists for rows being edited.
  const [editing, setEditing] = useState(false);
  const timerRef = useRef(null);
  const latestRef = useRef(text);

  // A different order behind the same mounted component → reseed.
  useEffect(() => {
    setText(initialNotes || '');
    setSaveState('idle');
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Follow edits made in ANOTHER copy of this editor on the same page.
  useEffect(() => {
    const onBroadcast = (e) => {
      if (e.detail?.orderId !== orderId) return;
      if (e.detail.notes === latestRef.current) return;
      latestRef.current = e.detail.notes;
      setText(e.detail.notes);
    };
    window.addEventListener(NOTES_EVENT, onBroadcast);
    return () => window.removeEventListener(NOTES_EVENT, onBroadcast);
  }, [orderId]);

  const save = async (value) => {
    setSaveState('saving');
    try {
      const res = await v2.customerOrders.saveFactoryNotes(orderId, value);
      // Only report "Saved" if nothing newer is pending.
      if (latestRef.current === value) {
        setSaveState('saved');
        setSavedAt(res.data?.saved_at ? new Date(res.data.saved_at) : new Date());
      }
    } catch (err) {
      console.error('OrderFactoryNotesV2: save failed', err);
      if (latestRef.current === value) setSaveState('error');
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    latestRef.current = value;
    broadcastOrderNotes(orderId, value);
    setSaveState('dirty');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(value), DEBOUNCE_MS);
  };

  if (orderId == null) return null;

  // Compact read-only preview — swapped for the real editor on click.
  if (compact && !editing) {
    return (
      <Box
        data-testid={`order-notes-${orderId}`}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'text' }}
      >
        <Box
          data-testid={`order-notes-preview-${orderId}`}
          sx={{
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.75,
            fontSize: '0.8rem',
            lineHeight: 1.4,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            color: text ? 'text.primary' : 'text.disabled',
            '&:hover': { borderColor: 'text.primary' },
          }}
        >
          {text || 'Order notes…'}
        </Box>
      </Box>
    );
  }

  const statusText = (() => {
    switch (saveState) {
      case 'dirty':
      case 'saving':
        return 'Saving…';
      case 'saved':
        return `Saved ${savedAt ? savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`;
      case 'error':
        return 'Save failed — still typing? it will retry on your next change';
      default:
        // Compact (in-table) mode keeps quiet until something happens.
        return compact ? '' : 'Shared across every work order of this customer order';
    }
  })();

  return (
    <Box
      data-testid={`order-notes-${orderId}`}
      // Compact (in-table) mode stretches to the row's full height — the
      // parent cell supplies height:100% down the chain.
      sx={compact ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}
    >
      {!compact && (
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <StickyNote2 fontSize="small" /> Order Notes
        </Typography>
      )}
      <TextField
        multiline
        minRows={compact ? 1 : 2}
        maxRows={compact ? undefined : 6}
        // Compact editors only exist because the preview was just clicked —
        // put the caret straight in the box.
        autoFocus={compact}
        fullWidth
        size="small"
        placeholder={compact ? 'Order notes…' : 'Notes for the factory about this order…'}
        value={text}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        sx={compact ? {
          flex: 1,
          // Fill the row height; the textarea scrolls when the note is
          // longer than the row is tall.
          '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
          '& .MuiInputBase-input': { height: '100% !important', overflowY: 'auto !important' },
        } : undefined}
        inputProps={{
          'data-testid': `order-notes-input-${orderId}`,
          style: compact ? { fontSize: '0.8rem' } : undefined,
        }}
      />
      {statusText !== '' && (
        <Typography
          variant="caption"
          color={saveState === 'error' ? 'error.main' : 'text.secondary'}
          data-testid={`order-notes-status-${orderId}`}
        >
          {statusText}
        </Typography>
      )}
    </Box>
  );
}
